/** Catálogo de atajos Factura POS (por usuario). */

export type PosHotkeyTecla =
  | 'F2'
  | 'F4'
  | 'F6'
  | 'F7'
  | 'F8'
  | 'F9'
  | 'Escape';

export type PosHotkeyAccionId =
  | 'aceptar'
  | 'cancelar'
  | 'dtoLinea'
  | 'bonif'
  | 'factPedidos'
  | 'factPendientes'
  | 'factCotizaciones'
  | 'cobro.efectivo'
  | 'cobro.tarjeta'
  | 'cobro.transferencia'
  | 'cobro.otros'
  | 'post.listado'
  | 'post.imprimirPdf'
  | 'post.imprimirTicket'
  | 'post.imprimirRecibo'
  | 'post.nuevaFactura';

export type PosHotkeyModoImpresionConfig = 'ticketera' | 'pdf' | null;

export interface PosHotkeyActionDef {
  id: PosHotkeyAccionId;
  caption: string;
  tooltip: string;
  /** Default de producto; null = sin atajo hasta que el usuario lo configure. */
  defaultKey: PosHotkeyTecla | null;
  variant?: 'accept' | 'cancel' | 'default';
  /** Si true, se muestra en la barra inferior cuando tiene tecla. */
  showInBar?: boolean;
  group: 'flujo' | 'cobro' | 'postEmision';
  /** Si está definido, la acción solo aparece en configuración cuando aplica la impresión POS. */
  requiresImpresion?: 'any' | 'ticketera';
}

/** Teclas que el browser/OS usan y no se pueden asignar. */
export const POS_HOTKEY_TECLAS_BLOQUEADAS = ['F1', 'F3', 'F5', 'F10', 'F11', 'F12'] as const;

/** Teclas permitidas para asignar. */
export const POS_HOTKEY_TECLAS_PERMITIDAS: PosHotkeyTecla[] = [
  'F2',
  'F4',
  'F6',
  'F7',
  'F8',
  'F9',
  'Escape',
];

export const POS_HOTKEY_ACTIONS: PosHotkeyActionDef[] = [
  {
    id: 'aceptar',
    caption: 'Aceptar',
    tooltip: 'Guardar / emitir la factura',
    defaultKey: 'F2',
    variant: 'accept',
    showInBar: true,
    group: 'flujo',
  },
  {
    id: 'cancelar',
    caption: 'Cancelar',
    tooltip: 'Cancelar y volver sin guardar',
    defaultKey: 'Escape',
    variant: 'cancel',
    showInBar: true,
    group: 'flujo',
  },
  {
    id: 'dtoLinea',
    caption: 'Dtos/Inc x Linea',
    tooltip: 'Editar descuento o incremento línea por línea',
    defaultKey: 'F4',
    showInBar: true,
    group: 'flujo',
  },
  {
    id: 'bonif',
    caption: 'Bonif Recargos',
    tooltip: 'Abrir totales → Desc 1→2→3 → cerrar',
    defaultKey: 'F7',
    showInBar: true,
    group: 'flujo',
  },
  {
    id: 'factPedidos',
    caption: 'Fact. Pedidos',
    tooltip: 'Buscar y facturar un pedido',
    defaultKey: 'F6',
    showInBar: true,
    group: 'flujo',
  },
  {
    id: 'factPendientes',
    caption: 'Fact. P. En Esp.',
    tooltip: 'Buscar pendientes / pedidos en espera',
    defaultKey: 'F8',
    showInBar: true,
    group: 'flujo',
  },
  {
    id: 'factCotizaciones',
    caption: 'Fact. Cotizaciones',
    tooltip: 'Buscar y facturar una cotización',
    defaultKey: 'F9',
    showInBar: true,
    group: 'flujo',
  },
  {
    id: 'cobro.efectivo',
    caption: 'Cobro efectivo',
    tooltip: 'Agregar el saldo pendiente como pago en efectivo',
    defaultKey: null,
    showInBar: true,
    group: 'cobro',
  },
  {
    id: 'cobro.transferencia',
    caption: 'Cobro transferencia',
    tooltip: 'Agregar el saldo pendiente como transferencia',
    defaultKey: null,
    showInBar: true,
    group: 'cobro',
  },
  {
    id: 'cobro.tarjeta',
    caption: 'Cobro tarjeta',
    tooltip: 'Preparar cobro con tarjeta (requiere marca y autorización)',
    defaultKey: null,
    showInBar: true,
    group: 'cobro',
  },
  {
    id: 'cobro.otros',
    caption: 'Cobro otros',
    tooltip: 'Agregar el saldo pendiente con medio «otros»',
    defaultKey: null,
    showInBar: true,
    group: 'cobro',
  },
  {
    id: 'post.listado',
    caption: 'Ver listado facturas',
    tooltip: 'Ir al listado de facturas de venta',
    defaultKey: null,
    group: 'postEmision',
  },
  {
    id: 'post.imprimirPdf',
    caption: 'Imprimir PDF',
    tooltip: 'Abrir PDF de la factura emitida',
    defaultKey: null,
    requiresImpresion: 'any',
    group: 'postEmision',
  },
  {
    id: 'post.imprimirTicket',
    caption: 'Imprimir ticket',
    tooltip: 'Imprimir ticket ESC/POS de la factura emitida',
    defaultKey: null,
    requiresImpresion: 'ticketera',
    group: 'postEmision',
  },
  {
    id: 'post.imprimirRecibo',
    caption: 'Imprimir recibo',
    tooltip: 'Abrir PDF del recibo de cobro emitido',
    defaultKey: null,
    group: 'postEmision',
  },
  {
    id: 'post.nuevaFactura',
    caption: 'Nueva factura',
    tooltip: 'Limpiar pantalla y cargar una nueva factura POS',
    defaultKey: null,
    group: 'postEmision',
  },
];

export function isPosHotkeyActionConfigurable(
  action: PosHotkeyActionDef,
  modoImpresionPos: PosHotkeyModoImpresionConfig,
): boolean {
  if (!action.requiresImpresion) return true;
  if (action.requiresImpresion === 'ticketera') return modoImpresionPos === 'ticketera';
  if (action.requiresImpresion === 'any') {
    return modoImpresionPos === 'ticketera' || modoImpresionPos === 'pdf';
  }
  return true;
}

export const POS_HOTKEY_POST_EMISION_IDS: PosHotkeyAccionId[] = [
  'post.listado',
  'post.imprimirPdf',
  'post.imprimirTicket',
  'post.imprimirRecibo',
  'post.nuevaFactura',
];

export type PosHotkeyBindings = Partial<Record<PosHotkeyAccionId, PosHotkeyTecla>>;

export function normalizePosHotkeyTecla(raw: string | null | undefined): string {
  const t = String(raw ?? '').trim();
  if (!t) return '';
  if (/^esc(ape)?$/i.test(t)) return 'Escape';
  const f = t.match(/^f(\d{1,2})$/i);
  if (f) return `F${f[1]}`;
  return t;
}

export function isPosHotkeyTeclaBloqueada(tecla: string): boolean {
  return (POS_HOTKEY_TECLAS_BLOQUEADAS as readonly string[]).includes(
    normalizePosHotkeyTecla(tecla),
  );
}

export function isPosHotkeyTeclaPermitida(tecla: string): tecla is PosHotkeyTecla {
  const n = normalizePosHotkeyTecla(tecla);
  return (POS_HOTKEY_TECLAS_PERMITIDAS as string[]).includes(n);
}

/** Defaults de producto (solo acciones con defaultKey). */
export function getPosHotkeyDefaultBindings(): PosHotkeyBindings {
  const out: PosHotkeyBindings = {};
  for (const a of POS_HOTKEY_ACTIONS) {
    if (a.defaultKey) out[a.id] = a.defaultKey;
  }
  return out;
}

/**
 * Fusiona defaults + overrides del usuario.
 * - Sin filas en BD → defaults de producto.
 * - Con filas → solo lo guardado (permite desasignar un default).
 */
export function mergePosHotkeyBindings(
  stored: Array<{ accionId: string; tecla: string }> | null | undefined,
): PosHotkeyBindings {
  if (!stored?.length) return getPosHotkeyDefaultBindings();

  const out: PosHotkeyBindings = {};
  const validIds = new Set(POS_HOTKEY_ACTIONS.map((a) => a.id));

  for (const row of stored) {
    const id = String(row.accionId ?? '').trim() as PosHotkeyAccionId;
    const tecla = normalizePosHotkeyTecla(row.tecla);
    if (!id || !validIds.has(id)) continue;
    if (!tecla || isPosHotkeyTeclaBloqueada(tecla)) continue;
    if (!isPosHotkeyTeclaPermitida(tecla)) continue;
    out[id] = tecla;
  }

  return out;
}

/** Invierte acción→tecla a tecla→acción (primera gana si hubiera colisión). */
export function invertPosHotkeyBindings(
  bindings: PosHotkeyBindings,
): Partial<Record<PosHotkeyTecla, PosHotkeyAccionId>> {
  const out: Partial<Record<PosHotkeyTecla, PosHotkeyAccionId>> = {};
  for (const a of POS_HOTKEY_ACTIONS) {
    const tecla = bindings[a.id];
    if (!tecla) continue;
    if (!out[tecla]) out[tecla] = a.id;
  }
  return out;
}

export function formatPosHotkeyLabel(tecla: string): string {
  return tecla === 'Escape' ? 'ESC' : tecla;
}

/** Valida un mapa editable; devuelve mensaje de error o null. */
export function validatePosHotkeyBindingsDraft(
  draft: PosHotkeyBindings,
): string | null {
  const seen = new Map<string, PosHotkeyAccionId>();
  for (const a of POS_HOTKEY_ACTIONS) {
    const tecla = draft[a.id];
    if (!tecla) continue;
    const n = normalizePosHotkeyTecla(tecla);
    if (isPosHotkeyTeclaBloqueada(n)) {
      return `${a.caption}: la tecla ${n} está reservada (F1, F3, F5, F10, F11, F12).`;
    }
    if (!isPosHotkeyTeclaPermitida(n)) {
      return `${a.caption}: tecla no permitida (${n}).`;
    }
    const other = seen.get(n);
    if (other) {
      const otherCaption =
        POS_HOTKEY_ACTIONS.find((x) => x.id === other)?.caption ?? other;
      return `La tecla ${formatPosHotkeyLabel(n)} está en «${otherCaption}» y «${a.caption}».`;
    }
    seen.set(n, a.id);
  }
  // Aceptar y cancelar deben tener tecla
  if (!draft.aceptar) return 'La acción Aceptar debe tener una tecla.';
  if (!draft.cancelar) return 'La acción Cancelar debe tener una tecla.';
  return null;
}

export function resolvePosHotkeyFromEvent(event: KeyboardEvent): PosHotkeyTecla | null {
  const code = event.code;
  const key = event.key;
  const candidates = [code, key];
  for (const c of candidates) {
    const n = normalizePosHotkeyTecla(c);
    if (n === 'Escape') return 'Escape';
    if (/^F\d{1,2}$/.test(n) && isPosHotkeyTeclaPermitida(n)) {
      return n;
    }
    // F5 etc. bloqueadas: detectar para poder preventDefault si hace falta, pero no asignar
    if (/^F\d{1,2}$/.test(n)) {
      return null;
    }
  }
  if (key === 'Escape' || code === 'Escape') return 'Escape';
  return null;
}
